import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon } from 'lucide-react';
import { builders, BUILDERS_AS_OF } from '../data/builders';
import { BuilderType } from '../types/builders';
import { PageHeader } from '../components/PageHeader';
import { DataBadge } from '../components/DataBadge';
import { SourceRef } from '../components/SourceRef';
import { ChipGroup } from '../components/ChipGroup';
import { DownloadButtons } from '../components/DownloadButtons';
import { Pager } from '../components/Pager';
import { useCappedPages } from '../hooks/useCappedPages';
type TypeFilter = 'all' | BuilderType;
const TYPE_STYLE: Record<BuilderType, string> = {
  Lab: 'border-machine/40 text-machine',
  Startup: 'border-human/40 text-human',
  'Big tech': 'border-line text-ink',
  Government: 'border-live/40 text-live',
  University: 'border-line text-muted',
  Community: 'border-signal/40 text-signal'
};
export function Builders() {
  const [type, setType] = useState<TypeFilter>('all');
  const [country, setCountry] = useState('all');
  const [query, setQuery] = useState('');
  const countries = useMemo(() => {
    const m = new Map<string, number>();
    builders.forEach(b => m.set(b.country, (m.get(b.country) ?? 0) + 1));
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  }, []);
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return builders.filter(b => (type === 'all' || b.type === type) && (country === 'all' || b.country === country) && (!q || `${b.org} ${b.builds} ${b.product}`.toLowerCase().includes(q)));
  }, [type, country, query]);
  const pager = useCappedPages(rows);
  const top = countries.slice(0, 6);
  return <>
      <PageHeader title="AI Builders by Country" lede="The organizations making the models and agents behind the machine population, by headquarters. Every row links to the organization’s own page." data-id="element-912" data-mp="2zf91k71p3c32">
        <DataBadge mode="snapshot" source="Curated builders dataset" asOf={BUILDERS_AS_OF} data-id="element-913" data-mp="14bb1sv8yvm2v" />
      </PageHeader>

      <section className="mx-auto max-w-[1400px] px-5 md:px-8" data-id="element-914" data-mp="zby4thodsymk">
        <div className="grid gap-8 border-b border-line py-6 md:grid-cols-[auto_minmax(0,1fr)] md:items-end" data-id="element-915" data-mp="1dcimubmh2qf0">
          <div data-id="element-916" data-mp="19annvds0kp4e">
            <p className="text-[12.5px] text-muted" data-id="element-917" data-mp="19c8i12af261q">Organizations tracked</p>
            <p className="mt-1 font-mono text-4xl tabular text-ink" data-id="element-918" data-mp="llcre8hp5gw2">
              {builders.length}
              <SourceRef id="censusCurated" data-id="element-919" data-mp="gn1l27qm1oil" />
            </p>
            <p className="font-mono text-[11px] text-faint" data-id="element-920" data-mp="gkf2ld26loll">across {countries.length} countries &amp; the open-source commons</p>
          </div>
          <ul className="flex flex-wrap gap-x-6 gap-y-2 md:justify-end" aria-label="Most builders" data-id="element-921" data-mp="pmoea696ro0k">
            {top.map(([name, n]) => <li key={name} data-id="element-922" data-mp="1vsnncgguyuw">
                <button type="button" onClick={() => setCountry(country === name ? 'all' : name)} className={`text-left text-[13px] ${country === name ? 'text-machine' : 'text-muted hover:text-ink'}`} data-id="element-923" data-mp="3u1eddu5tmvea">
                  {name} <span className="font-mono text-ink" data-id="element-924" data-mp="3onxv889ac0e1">{n}</span>
                </button>
              </li>)}
          </ul>
        </div>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 py-4" data-id="element-925" data-mp="3a0uwxzd51npn">
          <label className="relative block w-full sm:w-64" data-id="element-926" data-mp="3ezfyq381gfg7">
            <span className="sr-only" data-id="element-927" data-mp="6il57ed4s7es">Search builders</span>
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-928" data-mp="6j4ds4gw5jg5" />
            <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Org, product or focus" className="w-full rounded-md border border-line bg-surface py-2 pl-9 pr-3 text-sm text-ink placeholder:text-faint focus:border-machine focus:outline-none" data-id="element-929" data-mp="3spt6o5kxju5j" />
          </label>
          <label className="flex items-center gap-2 text-[13px] text-muted" data-id="element-930" data-mp="2m7mjof3pu00l">
            Country
            <select value={country} onChange={e => setCountry(e.target.value)} className="rounded-md border border-line bg-surface px-2.5 py-1.5 text-[13px] text-ink focus:border-machine focus:outline-none" data-id="element-931" data-mp="32iydbz1oyp4o">
              <option value="all" data-id="element-932" data-mp="2sopmrgw81695">All countries</option>
              {countries.map(([name, n]) => <option key={name} value={name} data-id="element-933" data-mp="3lfptw0pumq51">
                  {name} ({n})
                </option>)}
            </select>
          </label>
          <ChipGroup label="Type" value={type} onChange={setType} options={[['all', 'All'], ['Lab', 'Lab'], ['Startup', 'Startup'], ['Big tech', 'Big tech'], ['Government', 'Government'], ['University', 'University'], ['Community', 'Community']]} data-id="element-934" data-mp="3ghxzn01qerxo" />
          <div className="ml-auto" data-id="element-935" data-mp="203y4s3hqtsha">
            <DownloadButtons name="builders" getRows={() => rows.map(b => ({
            org: b.org,
            country: b.country,
            cca3: b.cca3,
            type: b.type,
            builds: b.builds,
            product: b.product,
            status: b.status,
            source: b.sourceUrl
          }))} data-id="element-936" data-mp="1v3a6cwr6q9d1" />
          </div>
        </div>

        <div className="overflow-x-auto rounded-lg border border-line" data-id="element-937" data-mp="fhzqj7b5n7yr">
          <table className="w-full min-w-[980px] border-collapse text-[13.5px]" data-id="element-938" data-mp="ki4aj7b9uktq">
            <thead className="bg-surface text-left text-[12px] text-muted" data-id="element-939" data-mp="paxe3rv07n48">
              <tr data-id="element-940" data-mp="ua0aknwx4l0l">
                {['Organization', 'Country', 'Type', 'What they build', 'Notable product / model', 'Status'].map(h => <th key={h} scope="col" className="whitespace-nowrap border-b border-line px-3 py-2.5 font-normal" data-id="element-941" data-mp="y90ru1zqzflh">
                    {h}
                  </th>)}
              </tr>
            </thead>
            <tbody data-id="element-942" data-mp="186ng3f2xsji4">
              {pager.visible.map(b => <tr key={`${b.org}-${b.country}`} className="border-b border-line/60 last:border-b-0 hover:bg-surface" data-id="element-943" data-mp="352urok96aag2">
                  <th scope="row" className="whitespace-nowrap px-3 py-2.5 text-left font-normal text-ink" data-id="element-944" data-mp="3a2h5ovwtrupx">
                    {b.org}
                    <SourceRef href={b.sourceUrl} label={`${b.org} — official site`} grade="A" figure={b.org} data-id="element-945" data-mp="2xylg1gx7xfom" />
                  </th>
                  <td className="whitespace-nowrap px-3 py-2.5" data-id="element-946" data-mp="3bynzh7l6ch5z">
                    {b.cca3 && b.cca2 ? <Link to={`/countries/${b.cca3}`} className="inline-flex items-center gap-2 text-muted hover:text-machine" data-id="element-947" data-mp="36xgnsxfwuxu1">
                        <img src={`https://flagcdn.com/${b.cca2.toLowerCase()}.svg`} alt="" loading="lazy" className="h-3.5 w-5 rounded-[2px] object-cover ring-1 ring-line" data-id="element-948" data-mp="3gwxspcnc97oy" />
                        {b.country}
                      </Link> : <span className="text-muted" data-id="element-949" data-mp="3ppsx644zezdo">{b.country}</span>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5" data-id="element-950" data-mp="8f1myfzoi748">
                    <span className={`rounded border px-1.5 py-0.5 font-mono text-[10.5px] ${TYPE_STYLE[b.type]}`} data-id="element-951" data-mp="3j49qijppgvo">{b.type}</span>
                  </td>
                  <td className="px-3 py-2.5 text-ink/85" data-id="element-952" data-mp="r5g6qv3kofpu">{b.builds}</td>
                  <td className="px-3 py-2.5 font-mono text-[12.5px] text-ink" data-id="element-953" data-mp="r4n7sgauuui9">{b.product}</td>
                  <td className="whitespace-nowrap px-3 py-2.5" data-id="element-954" data-mp="d5f69gr7zaaw">
                    <span className={`font-mono text-[11px] ${b.status === 'Active' ? 'text-live' : 'text-machine'}`} data-id="element-955" data-mp="i3yr1du2ug1o">{b.status}</span>
                  </td>
                </tr>)}
              {rows.length === 0 && <tr data-id="element-956" data-mp="87vgbjl5t934">
                  <td colSpan={6} className="px-3 py-10 text-center text-muted" data-id="element-957" data-mp="88f25bdxkhmf">
                    No builders match these filters.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
        <Pager page={pager.page} pages={pager.pages} setPage={pager.setPage} total={pager.total} isCapped={pager.isCapped} data-id="element-958" data-mp="3gm43gc4wx7nw" />
        <p className="mt-2 text-[12px] text-muted" data-id="element-959" data-mp="3lkywe6wlgclq">Country is the organization’s headquarters. Inclusion means the organization builds or ships AI models or agents — not a ranking.</p>
      </section>
    </>;
}